import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ChevronLeft, Loader2, AlertTriangle, CheckCircle, XCircle, Sparkles, RotateCcw, Download, Trash2, Eye } from 'lucide-react'
import { getItem, getGenerations, toggleApproval, generateItem } from '@/api/client'
import { useApi, useMutation } from '@/hooks/useApi'
import { GenerationModal } from '@/components/GenerationModal'
import { PromptSelector } from '@/components/PromptSelector'
import { photoUrl } from '@/lib/photos'
import { mediaGenFileUrl } from '@/lib/media'
import { AuthedImage } from '@/components/AuthedImage'
import { fmtLocal } from '@/lib/time'

export function ItemDetail() {
  const { id } = useParams()
  const [showRegenerateModal, setShowRegenerateModal] = useState(false)
  const [regenerateConfig, setRegenerateConfig] = useState({
    model: 'gemini-2.5-flash-image',
    prompt: '',
    mode: 'sync',
    aspect_ratio: '1:1',
  })
  const [models, setModels] = useState([])
  const [activeVersion, setActiveVersion] = useState(0)
  const [costEstimate, setCostEstimate] = useState(null)

  const { data: item, loading: itemLoading, refetch: refetchItem } = useApi(
    () => getItem(parseInt(id)),
    [id]
  )

  const { data: generations, loading: gensLoading, refetch: refetchGenerations } = useApi(
    () => getGenerations(parseInt(id)),
    [id]
  )

  const { mutate: doRegenerate, loading: regenerating } = useMutation(generateItem)
  const { mutate: doToggleApproval, loading: toggling } = useMutation(toggleApproval)

  useEffect(() => {
    import('@/api/client').then(m => m.getModels().then(res => setModels(res.data))).catch(console.error)
  }, [])

  useEffect(() => {
    if (regenerateConfig.prompt) {
      import('@/api/client').then(m => m.estimateCost({
        model: regenerateConfig.model,
        mode: regenerateConfig.mode,
        item_ids: [parseInt(id)],
      }).then(res => setCostEstimate(res.data.estimated_cost_usd)).catch(() => setCostEstimate(null)))
    } else {
      setCostEstimate(null)
    }
  }, [regenerateConfig.model, regenerateConfig.mode, regenerateConfig.prompt])

  const handleRegenerate = async () => {
    if (!regenerateConfig.prompt.trim()) return
    try {
      await doRegenerate(parseInt(id), {
        ...regenerateConfig,
        item_ids: undefined, // single item
      })
      setShowRegenerateModal(false)
      refetchGenerations()
      refetchItem()
    } catch (err) {
      console.error('Regeneration failed:', err)
    }
  }

  const handleApprovalToggle = async (genId, approved) => {
    try {
      await doToggleApproval(genId, !approved)
      refetchGenerations()
      refetchItem()
    } catch (err) {
      console.error('Approval toggle failed:', err)
    }
  }

  if (itemLoading) {
    return <Loader2 className="h-8 w-8 animate-spin text-orange-500 mx-auto my-16" />
  }

  if (!item) {
    return <div className="text-center py-16 text-gray-500">Item not found</div>
  }

  // Sort generations by version
  const sortedGenerations = [...(generations || [])].sort((a, b) => a.version - b.version)
  const approvedGeneration = sortedGenerations.find(g => g.approved)
  const latestGeneration = sortedGenerations[sortedGenerations.length - 1]

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Link to={`/restaurant/${item.restaurant_id}`} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <ChevronLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{item.name}</h1>
            <p className="text-gray-500">{item.restaurant_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setRegenerateConfig(prev => ({ ...prev, prompt: '' }))
              setShowRegenerateModal(true)
            }}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            <Sparkles className="h-4 w-4" />
            Regenerate
          </button>
        </div>
      </div>

      {/* Item info */}
      <div className="mb-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <InfoTile label="Category" value={item.category} />
        <InfoTile label="Price" value={item.price} />
        <InfoTile label="Available" value={item.available ? 'Yes' : 'No'} />
        <InfoTile label="Status" value={item.status} />
        <InfoTile label="Ingredients" value={item.ingredients} className="sm:col-span-2" />
        <InfoTile label="Extras" value={item.extras} className="sm:col-span-2" />
        {item.description && (
          <InfoTile label="Description" value={item.description} className="lg:col-span-4" />
        )}
      </div>

      {/* Original Image */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Original Image</h2>
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden max-w-md">
          <AuthedImage
            src={photoUrl(item.image_path)}
            alt={`${item.name} (original)`}
            className="w-full h-auto"
            fallback="No original image"
          />
        </div>
      </div>

      {/* AI Alternatives from the batch system */}
      {item.ai_images && item.ai_images.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            AI Alternatives ({item.ai_images.length})
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h3 className="font-medium text-gray-900">Original</h3>
              </div>
              <div className="aspect-square bg-gray-100 overflow-hidden p-4">
                <AuthedImage
                  src={photoUrl(item.image_path)}
                  alt={`${item.name} (original)`}
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
            {item.ai_images.map((img) => (
              <div key={img.url} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-2">
                  <h3 className="font-medium text-gray-900">AI generated</h3>
                  <span className="text-xs text-gray-500 truncate" title={img.filename}>{img.filename}</span>
                </div>
                <div className="aspect-square bg-gray-100 overflow-hidden p-4">
                  <AuthedImage
                    src={img.url}
                    alt={`${item.name} (AI alternative)`}
                    className="w-full h-full object-contain"
                    fallback="No image"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Generations Comparison */}
      {sortedGenerations.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Generations ({sortedGenerations.length})</h2>
          
          {/* Version Tabs */}
          <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
            {sortedGenerations.map((gen, idx) => (
              <button
                key={gen.id}
                onClick={() => setActiveVersion(idx)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  activeVersion === idx
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                v{gen.version} {gen.approved && <CheckCircle className="inline h-3 w-3 ml-1" />}
              </button>
            ))}
          </div>

          {/* Side-by-side Comparison */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Original */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h3 className="font-medium text-gray-900">Original</h3>
              </div>
              <div className="aspect-square bg-gray-100 overflow-hidden p-4">
                <AuthedImage
                  src={photoUrl(item.image_path)}
                  alt={`${item.name} (original)`}
                  className="w-full h-full object-contain"
                />
              </div>
            </div>

            {/* Selected Generation */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                <h3 className="font-medium text-gray-900">Generation v{sortedGenerations[activeVersion].version}</h3>
                <div className="flex items-center gap-2">
                  {sortedGenerations[activeVersion].approved ? (
                    <button
                      onClick={() => handleApprovalToggle(sortedGenerations[activeVersion].id, true)}
                      disabled={toggling}
                      className="flex items-center gap-1 px-3 py-1 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                    >
                      <CheckCircle className="h-3 w-3" />
                      Approved
                    </button>
                  ) : (
                    <button
                      onClick={() => handleApprovalToggle(sortedGenerations[activeVersion].id, false)}
                      disabled={toggling}
                      className="flex items-center gap-1 px-3 py-1 text-sm bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors"
                    >
                      <XCircle className="h-3 w-3" />
                      Mark Approved
                    </button>
                  )}
                </div>
              </div>
              <div className="aspect-square bg-gray-100 overflow-hidden p-4 relative">
                {sortedGenerations[activeVersion].output_filename && (
                  <AuthedImage
                    src={mediaGenFileUrl(sortedGenerations[activeVersion].restaurant_name, sortedGenerations[activeVersion].output_filename)}
                    alt={`${item.name} (v${sortedGenerations[activeVersion].version})`}
                    className="w-full h-full object-contain"
                    fallback="No image"
                  />
                )}
              </div>
              <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 text-sm text-gray-500">
                <p>Model: {sortedGenerations[activeVersion].model}</p>
                <p>Cost: ${sortedGenerations[activeVersion].cost_usd?.toFixed(4) || 'N/A'}</p>
                <p>Generated: {fmtLocal(sortedGenerations[activeVersion].created_at)}</p>
              </div>
            </div>
          </div>

          {/* Generation Details */}
          <div className="mt-6 bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <h3 className="font-medium text-gray-900">Prompt Used</h3>
            </div>
            <div className="p-4 bg-gray-50">
              <pre className="text-sm text-gray-700 whitespace-pre-wrap">{sortedGenerations[activeVersion].prompt}</pre>
            </div>
          </div>
        </div>
      )}

      {/* No Generations Yet */}
      {sortedGenerations.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No generations yet</h3>
          <p className="text-gray-500 mb-6">Click "Regenerate" to create the first version</p>
          <button
            onClick={() => setShowRegenerateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            <Sparkles className="h-4 w-4" />
            Generate First Version
          </button>
        </div>
      )}

      {/* All Versions History */}
      {sortedGenerations.length > 1 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Version History</h2>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Version</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Model</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Cost</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Date</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedGenerations.map((gen) => (
                  <tr key={gen.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">v{gen.version}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{gen.model}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${
                        gen.approved ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {gen.approved ? <CheckCircle className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                        {gen.approved ? 'Approved' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">${gen.cost_usd?.toFixed(4) || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{fmtLocal(gen.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => {
                            const idx = sortedGenerations.findIndex(g => g.id === gen.id)
                            setActiveVersion(idx)
                          }}
                          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                          title="View"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {!gen.approved && (
                          <button
                            onClick={() => handleApprovalToggle(gen.id, false)}
                            disabled={toggling}
                            className="p-2 text-yellow-500 hover:text-yellow-700 rounded-lg hover:bg-yellow-50"
                            title="Approve"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Regenerate Modal */}
      <GenerationModal
        open={showRegenerateModal}
        onClose={() => setShowRegenerateModal(false)}
        config={regenerateConfig}
        onConfigChange={setRegenerateConfig}
        models={models}
        selectedCount={1}
        costEstimate={costEstimate}
        generating={regenerating}
        onGenerate={handleRegenerate}
      />
    </div>
  )
}

function InfoTile({ label, value, className = '' }) {
  const empty = value === null || value === undefined || value === ''
  return (
    <div className={`bg-white rounded-xl border border-gray-200 p-4 ${className}`}>
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">{label}</p>
      <p className="text-sm text-gray-800 mt-1">{empty ? <span className="text-gray-300">—</span> : value}</p>
    </div>
  )
}